import { useQuery } from '@apollo/client';

import { graphql } from '@eluve/graphql.tada';
import {
  useIsEluveAdmin,
  useIsTenantAdminFromSession,
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

const myReportingAccessQuery = graphql(`
  query GetMyReportingAccess($tenantId: uuid!, $userId: uuid!) {
    tenantUsersByPk(tenantId: $tenantId, userId: $userId) {
      __typename
      tenantId
      userId
      hasReportingAccess
    }
  }
`);

/**
 * Whether the current user may open the reporting dashboard for the tenant in
 * the route. Eluve admins and tenant admins always may; a tenant member needs
 * an explicit `tenant_users.has_reporting_access` grant from a tenant admin.
 *
 * The grant is read live rather than from a session claim so a tenant admin's
 * toggle takes effect without waiting for the tab's token to be re-minted.
 * This mirrors the UI gate only — the reporting API enforces the same rule
 * server-side in ReportingAccessGuard.
 */
export const useHasReportingAccess = () => {
  const isEluveAdmin = useIsEluveAdmin();
  const isTenantAdmin = useIsTenantAdminFromSession();
  const tenantId = useTenantIdFromParams();
  const userId = useUserIdFromSession();

  const isAdmin = isEluveAdmin || isTenantAdmin;

  const { data, loading } = useQuery(myReportingAccessQuery, {
    // Placeholder tenantId is never sent: `skip` covers the tenantless case.
    variables: { tenantId: tenantId ?? '', userId },
    skip: isAdmin || !tenantId,
  });

  return {
    hasReportingAccess:
      isAdmin || Boolean(data?.tenantUsersByPk?.hasReportingAccess),
    // Admins short-circuit the query, so `loading` is only meaningful for the
    // member path. Callers that redirect on denial must wait this out or they
    // will bounce a granted member before their grant has been read.
    isLoading: loading,
  };
};
